import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import AccordionHeader from '@Components/Results/common/accordion-header.component';
import store from '@Store';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import { resultsConfig } from '@Constants/Results';
import * as Spinners from '@Services/Spinners';
import RxSpinnerComponent from '@Components/Results/utils/rx-spinner.component';
import Slugify from '@Utils/Slugify';
import MarkushDetails from '@Components/Results/substances/tables/markush/markush-details.component';
import SubstanceHelper from '@Components/Results/services/substance-helper.factory';
import SubstanceQuantitativeData from '@Components/Results/substances/tables/quantitative/substance-quantitative-data.component';
import SubstanceBioData from '@Components/Results/substances/substance-bio-data.component';
import { getValueByNs } from '@Hooks/LibraryInitialization/MoreUnderscore';
import SubstanceCommercialSupplierInformation from '@Components/Results/substances/information/commercial/substance-commercial-supplier-information.component';
import { contextFromMetadata } from '@Components/Results/utils/metadata';
import { getChildHitCount, getChildCount } from '@Components/Results/substances/utils/factCountHelper';
import Catalyst from '@Services/Catalyst';
import history from '@AppRoutes/history';
import { getTrackingPathName } from '@AppRoutes/AppRoutesSelectors';

export default class SubstanceSubPropertyComponent extends React.PureComponent {
  constructor(props) {
    super(props);

    const {
      metadata,
      fact,
      child,
      substanceIndex,
      database: { id: databaseId },
      isDefaultOpen,
    } = props;

    child.extendedTitle = `${history.location.pathname.indexOf('heatmap') !== -1 ? 'Heatmap:' : ''}${
      (fact && fact.title ? `${fact.title}:` : '') + child.title
    }`;

    this.state = {
      isOpen: child.open || false,
      fact,
      child,
    };

    this.id = `accordion-${Slugify(fact.title)}-${Slugify(child.title)}-${substanceIndex}`;
    this.initialItemsAmount = resultsConfig.INITIAL_ITEMS_AMOUNT.substanceQuantitative;

    this.isDefaultOpen = isDefaultOpen;

    this.toggleAccordion = this.toggleAccordion.bind(this);
    this.retrieveFactData = SubstanceHelper.retrieveFactData.bind(this, substanceIndex, child, databaseId, metadata);
    this.trackViewedData = this.trackViewedData.bind(this);

    this.heatmapMode = metadata.context === 'dpitems';
  }

  componentDidMount() {
    if (this.isDefaultOpen) {
      this.toggleAccordion({ skipContentLinkTrack: true });
    }
  }

  toggleAccordion({ skipContentLinkTrack = false } = {}) {
    const { fact, child, isOpen } = this.state;
    const toggle = (data) => {
      this.setState({ isOpen: !isOpen });
      return data || Promise.resolve(this.state);
    };
    const trackContentLink = () => {
      const { metadata } = this.props;
      // eslint-disable-next-line no-shadow
      const { isOpen, fact, child } = this.state;
      const { title } = child;
      if (!isOpen && !skipContentLinkTrack) {
        const name = (fact && fact.title ? `${fact.title}:` : '') + title;

        if (metadata.isViewPreview) {
          Catalyst.trackContentLinkClick(name, `query:preview:${contextFromMetadata(metadata)}`);
        } else {
          Catalyst.trackContentLinkClick(name);
        }
      }
    };

    let actualRequest = Promise.resolve('woop woop wopp wopp'); // else case
    if (fact.type === 'bio' && !child.bioData) {
      actualRequest = this.retrieveBioData(child, fact);
    } else if (fact.type === 'fact' && child.id === 'PROD') {
      if (!child.commercialData) {
        // inner if because of collapsing logic
        actualRequest = this.initCommercialSupplierData(child, fact);
      }
    } else if (fact.type === 'fact' && !child.quanData) {
      actualRequest = this.initQuanData(child, fact);
    }
    return actualRequest.then(trackContentLink).then(toggle).catch(Logger.error);
  }

  retrieveBioData(child, fact) {
    const {
      metadata,
      database: { id: databaseId },
    } = this.props;
    const resultName = this.heatmapMode ? fact.resultName : fact.nestedResultName;
    const trackingPathName = getTrackingPathName(store.getState());
    const trackData = {
      context: metadata.context,
      pageName: trackingPathName,
      factTitle: fact.title,
      childTitle: child.title,
    };

    Spinners.activate(this.id);

    return SubstanceHelper.retrieveBioCategoryData(resultName, databaseId, child.index, trackData)
      .then((bioData) => {
        const newState = { child: { ...child, bioData } };
        this.setState(newState);
        this.trackViewedData(newState.child, newState.child.type, newState.child.title);
      })
      .catch((error) => Logger.error(error))
      .finally(() => {
        Spinners.terminate(this.id);
      });
  }

  initQuanData(child) {
    if (!child.count) return Promise.resolve();

    Spinners.activate(this.id);

    return this.retrieveFactData(1, this.initialItemsAmount)
      .then((data) => {
        const newState = { child: { ...child, quanData: data } };
        this.setState(newState);
        this.trackViewedData(newState.child, newState.child.type, newState.child.title, 1, this.initialItemsAmount);
      })
      .catch((error) => Logger.error(error))
      .finally(() => {
        Spinners.terminate(this.id);
      });
  }

  initCommercialSupplierData(child) {
    if (!child.count) return Promise.resolve();

    // eslint-disable-next-line no-param-reassign
    child.format = 'rcssupplier'; // will render different template and request with a different id

    Spinners.activate(this.id);

    return this.retrieveFactData(1, this.initialItemsAmount)
      .then((data) => {
        const newState = { child: { ...child, commercialData: data.commercialData } };
        this.setState(newState);
        this.trackViewedData(newState.child, newState.child.type, newState.child.title, 1, this.initialItemsAmount);
      })
      .catch((error) => Logger.error(error))
      .finally(() => {
        Spinners.terminate(this.id);
      });
  }

  trackViewedData(data, type, title, firstItem, lastItem) {
    const { child } = this.state;
    const { substanceId } = this.props;
    Catalyst.trackAmountOfViewedData(
      substanceId,
      data,
      type === 'showMoreBio' ? type + child.type : type,
      title || child.title,
      store.getState(),
      firstItem,
      lastItem
    );
  }

  render() {
    const { isOpen, child, fact } = this.state;
    const { metadata, substance, substanceId, details, substanceIndex } = this.props;

    const itemClasses = classnames(['rx-accordion-item', !isOpen ? 'closed' : null, child.type === 'bio' ? 'no-nested-data' : null]);

    const hasBioData = getValueByNs(child, 'bioData.quan.data') || getValueByNs(child, 'bioData.qual.data');

    return (
      <li className={itemClasses}>
        {this.isDefaultOpen ? null : (
          <AccordionHeader isOpen={isOpen} accordionId={this.id} toggle={this.toggleAccordion}>
            {child.title}
            {(child.count > 0 || child.size > 0) && (
              <span>
                {' '}
                -&nbsp;
                {fact.id === 'HIT_DATA' && <span>{getChildHitCount(child, substance)} hits out of </span>}
                {getChildCount(child, substance)}
              </span>
            )}
          </AccordionHeader>
        )}

        <RxSpinnerComponent spinnerId={this.id} position="relative" />

        {isOpen && (
          <div id={this.id} className="rx-accordion-content" aria-expanded={isOpen}>
            {child.quanData && (
              <SubstanceQuantitativeData
                metadata={metadata}
                data={child.quanData}
                total={child.hitData ? child.hit : child.count}
                uniqueId={this.id}
                retrieveData={this.retrieveFactData}
                track={this.trackViewedData}
                substanceId={substanceId}
                initialItemsAmount={this.initialItemsAmount}
              />
            )}
            {hasBioData && <SubstanceBioData metadata={metadata} fact={child} substanceId={substanceId} />}
            {child.type === 'markush' && <MarkushDetails data={child.data} />}
            {child.format === 'rcssupplier' && (
              <SubstanceCommercialSupplierInformation
                metadata={metadata}
                data={child.commercialData}
                details={details}
                total={child.hitData ? child.hit : child.count}
                uniqueId={this.id}
                retrieveData={this.retrieveFactData}
                track={this.trackViewedData}
                substanceId={substanceId}
                substanceIndex={substanceIndex}
                initialItemsAmount={this.initialItemsAmount}
                isHitData={child.hitData}
              />
            )}
          </div>
        )}
      </li>
    );
  }
}

SubstanceSubPropertyComponent.propTypes = {
  metadata: MetadataPropTypesShape,
  substance: PropTypes.object.isRequired,
  child: PropTypes.object.isRequired,
  fact: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    resultName: PropTypes.string,
    nestedResultName: PropTypes.string,
    hitData: PropTypes.bool.isRequired,
    title: PropTypes.string,
  }).isRequired,
  substanceIndex: PropTypes.string.isRequired,
  database: PropTypes.object.isRequired,
  substanceId: PropTypes.string.isRequired,
  isDefaultOpen: PropTypes.bool,
  details: PropTypes.object,
};
